import { Badge } from '../../components/ui/Badge.js';
import { Card } from '../../components/ui/Card.js';
import { useFetch } from '../../hooks/useFetch.js';
import {
  PENALTY_UNPAID_SENTENCE,
  type PenaltyList,
  STATE_LABEL,
  STATE_VARIANT,
  dayWords,
  owesPenalty,
  rm,
} from './penalties.js';

/**
 * 393 (#1313, US1, mock-up screen 1): the customer's active penalties on the Account page, newest first, each opening
 * its page. A banner while any is unpaid; nothing at all when there are none (a failed read shows nothing either: the
 * booking form still states the block).
 */
export function PenaltiesSection() {
  const read = useFetch<PenaltyList>('/penalties/mine');
  if (
    read.status !== 'ok' ||
    !read.data ||
    !Array.isArray(read.data.items) ||
    typeof read.data.unpaid_count !== 'number'
  )
    return null;
  const { items } = read.data;
  if (items.length === 0) return null;
  return (
    <Card className="mb-6">
      <div data-testid="penalties-section">
        <h2 className="mb-2 text-sm font-semibold text-text">Penalties</h2>
        {owesPenalty(read.data) && (
          <p
            data-testid="penalty-banner"
            className="mb-3 rounded-lg bg-red-900/40 px-3 py-2 text-sm text-red-100"
          >
            {PENALTY_UNPAID_SENTENCE}
          </p>
        )}
        <ul className="flex flex-col gap-2">
          {items.map((p) => (
            <li key={p.id}>
              <a
                href={`/account/penalties/${p.id}`}
                data-testid="penalty-row"
                className="block rounded-lg bg-surface-mid px-3 py-3"
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-text">{p.type_label}</span>
                  <Badge variant={STATE_VARIANT[p.state]}>{STATE_LABEL[p.state]}</Badge>
                </span>
                <span className="mt-1 flex items-center justify-between gap-2 text-xs text-gray-400">
                  <span className="min-w-0">
                    {dayWords(p.on)} · <span className="text-primary">{p.plate_number}</span> · #
                    {p.booking_ref}
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-sm font-semibold text-text">
                    {rm(p.amount_minor)}
                  </span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}
